/**
 * lib/torNative.js — ponte JS verso il plugin nativo "TorVpn" (Fase 2).
 *
 * Approccio WebView-PROXY (non VPN): il plugin avvia il motore Tor incorporato e,
 * a bootstrap completato, instrada la WebView di Nullum sul proxy HTTP di Tor.
 * SOLO nell'app Android nativa (isNativeApp): sul web/PWA queste funzioni sono no-op.
 *
 * Quando il proxy si aggancia (evento "torProxy" con proxy:true) l'app entra in
 * "modalità Tor": alza un flag in sessionStorage e RICARICA, così Firebase si
 * re-inizializza puntando al RELAY (Google blocca gli IP Tor diretti). Il flag in
 * sessionStorage sopravvive al reload ma non al riavvio dell'app.
 */
import { registerPlugin } from '@capacitor/core';
import { isNativeApp } from './platform';

let _plugin = null;

// DEVE combaciare con TOR_RELAY_FLAG in lib/firebase.js.
const TOR_RELAY_FLAG = 'nullum.torRelay';

/** Entra in modalità Tor: instrada Firebase sul relay e RICARICA a "/" (non
 *  all'URL corrente, es. /tor): così dopo il reload `HomeRoute` idrata la sessione
 *  usa-e-getta e porta all'inbox, invece di ri-renderizzare la schermata Tor. */
function enterRelayMode() {
  try {
    if (sessionStorage.getItem(TOR_RELAY_FLAG) !== '1') {
      sessionStorage.setItem(TOR_RELAY_FLAG, '1');
      setTimeout(() => { try { window.location.replace('/'); } catch { /* no-op */ } }, 400);
    }
  } catch { /* no-op */ }
}

/** Esce dalla modalità Tor: Firebase torna diretto (reload). Usato dallo stop. */
export function exitRelayMode() {
  try {
    if (sessionStorage.getItem(TOR_RELAY_FLAG) === '1') {
      sessionStorage.removeItem(TOR_RELAY_FLAG);
      setTimeout(() => { try { window.location.reload(); } catch { /* no-op */ } }, 400);
    }
  } catch { /* no-op */ }
}

function getPlugin(log = () => {}) {
  if (!isNativeApp()) { log('› non-nativo → niente plugin'); return null; }
  if (_plugin) { log('› plugin (già in cache)'); return _plugin; }
  try {
    // `registerPlugin` è importato STATICAMENTE (in cima) → è già nel chunk di
    // TorScreen, caricato quando lo schermo è visibile. NIENTE `import()` a
    // runtime: la vecchia `await import('@capacitor/core')` caricava un chunk
    // lazy che nella WebView Capacitor NON si risolveva mai (richiesta al
    // local-server appesa) → getPlugin si impiantava lì. Sincrono = niente hang.
    log('› registerPlugin(TorVpn)…');
    _plugin = registerPlugin('TorVpn');
    log('› plugin TorVpn registrato');
    // Quando il proxy Tor si aggancia → entra in modalità relay (reload) così
    // Firebase si re-inizializza puntando al relay.
    try {
      _plugin.addListener('torProxy', (ev) => {
        if (ev && ev.proxy === true) enterRelayMode();
      });
    } catch { /* addListener non disponibile: non critico */ }
    return _plugin;
  } catch (e) {
    log('› getPlugin ERRORE: ' + (e?.message || e));
    console.warn('TorVpn plugin non disponibile', e);
    return null;
  }
}

/**
 * Avvia il motore Tor. `log` (opzionale) riceve i micro-passi, così la UI può
 * mostrare ESATTAMENTE dove si ferma (diagnostica su device senza logcat).
 */
export async function torStart(log = () => {}) {
  log('› getPlugin…');
  const p = getPlugin(log);
  log(`› plugin ${p ? 'OK' : 'NULL'} (native=${isNativeApp()})`);
  if (!p) return { ok: false, reason: 'NOT_NATIVE' };
  log('› chiamo il nativo start()…');
  try {
    // Timeout: se il nativo non risponde entro 8s = si è impiantato lì.
    const r = await Promise.race([
      p.start(),
      new Promise((_, rej) => setTimeout(() => rej(new Error('TIMEOUT 8s: il nativo non risponde')), 8000)),
    ]);
    log('› start() risposto: ' + JSON.stringify(r || {}));
    return { ok: true };
  } catch (e) {
    log('› start() ERRORE: ' + (e?.message || e));
    return { ok: false, reason: 'ERROR', error: e?.message };
  }
}

/** Ferma il motore Tor. */
export async function torStop() {
  const p = getPlugin();
  if (!p) return { ok: false, reason: 'NOT_NATIVE' };
  try { await p.stop(); return { ok: true }; }
  catch (e) { return { ok: false, reason: 'ERROR', error: e?.message }; }
}

/** Stato attuale: { status: 'STARTING'|'ON'|'OFF', socksPort: number }. */
export async function torGetStatus() {
  const p = getPlugin();
  if (!p) return { status: 'OFF', socksPort: 0 };
  try { return await p.getStatus(); }
  catch { return { status: 'OFF', socksPort: 0 }; }
}

/**
 * Ascolta i cambi di stato. cb riceve { status, socksPort }.
 * Ritorna una funzione di cleanup.
 */
export async function onTorStatus(cb) {
  const p = getPlugin();
  if (!p) return () => {};
  try {
    const h = await p.addListener('torStatus', cb);
    return () => { try { h.remove(); } catch {} };
  } catch {
    return () => {};
  }
}

/** Ascolta l'evento proxy ({ proxy, port }). Ritorna cleanup. */
export async function onTorProxy(cb) {
  const p = getPlugin();
  if (!p) return () => {};
  try {
    const h = await p.addListener('torProxy', cb);
    return () => { try { h.remove(); } catch {} };
  } catch {
    return () => {};
  }
}
